export const NumberFormatKind = {
  DEFAULT: 'DEFAULT',
  SUM: 'SUM',
  PRICE: 'PRICE',
  QUANTITY: 'QUANTITY',
};

export const getConfigDecimalPlaces = (config, fallbackDecimalPlaces = 2) => {
  const parsedDecimalPlaces = Number(config?.decimalPlaces);

  if (Number.isInteger(parsedDecimalPlaces) && parsedDecimalPlaces >= 0) {
    return parsedDecimalPlaces;
  }

  return fallbackDecimalPlaces;
};

const getKindSpecificDecimalPlaces = (kind, config) => {
  switch (kind) {
    case NumberFormatKind.SUM:
      return config?.sumPlace;
    case NumberFormatKind.PRICE:
      return config?.pricePlace;
    default:
      return undefined;
  }
};

export const getConfigDecimalPlacesByKind = (kind, config, fallbackDecimalPlaces = 2) => {
  const kindSpecificValue = Number(getKindSpecificDecimalPlaces(kind, config));

  if (Number.isInteger(kindSpecificValue) && kindSpecificValue >= 0) {
    return kindSpecificValue;
  }

  return getConfigDecimalPlaces(config, fallbackDecimalPlaces);
};

export const isFloatingPointByKind = (kind, config, fallback = true) => {
  switch (kind) {
    case NumberFormatKind.SUM:
      return config?.sumFloatingPoint ?? fallback;
    case NumberFormatKind.PRICE:
      return config?.priceFloatingPoint ?? fallback;
    case NumberFormatKind.QUANTITY:
      return config?.quantityFloatingPoint ?? fallback;
    default:
      return fallback;
  }
};

export const getInputStepByDecimalPlaces = (decimalPlaces = 0) => {
  const normalizedDecimalPlaces = Number.isInteger(decimalPlaces) && decimalPlaces >= 0 ? decimalPlaces : 0;

  if (normalizedDecimalPlaces === 0) {
    return '1';
  }

  return `0.${'0'.repeat(normalizedDecimalPlaces - 1)}1`;
};

export const roundToDecimalPlaces = (value, decimalPlaces = 0) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return value;
  }

  const normalizedDecimalPlaces = Number.isInteger(decimalPlaces) && decimalPlaces >= 0 ? decimalPlaces : 0;
  return Number(numericValue.toFixed(normalizedDecimalPlaces));
};

export const formatNumberByConfig = (rawValue, options = {}) => {
  const {
    config,
    kind = NumberFormatKind.DEFAULT,
    fallbackDecimalPlaces = 2,
    floatingPointFallback = true,
    locale = 'hu-HU',
  } = options;

  const numericValue = Number(rawValue);

  if (!Number.isFinite(numericValue)) {
    return rawValue;
  }

  const decimalPlaces = getConfigDecimalPlacesByKind(kind, config, fallbackDecimalPlaces);
  const isFloatingPoint = isFloatingPointByKind(kind, config, floatingPointFallback);
  const forceGroupingFromOneThousand =
    kind === NumberFormatKind.SUM || kind === NumberFormatKind.PRICE || kind === NumberFormatKind.QUANTITY;

  const formatterOptions = {
    style: 'decimal',
    minimumFractionDigits: isFloatingPoint ? 0 : decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  };

  if (forceGroupingFromOneThousand) {
    try {
      return new Intl.NumberFormat(locale, {
        ...formatterOptions,
        useGrouping: 'always',
      }).format(numericValue);
    } catch (_error) {
      // Fallback for runtimes where string-valued useGrouping is not supported.
      return new Intl.NumberFormat(locale, {
        ...formatterOptions,
        useGrouping: true,
      }).format(numericValue);
    }
  }

  return new Intl.NumberFormat(locale, {
    ...formatterOptions,
    useGrouping: true,
  }).format(numericValue);
};
